<template>
  <div class="mobile-method-c">
    <!-- Task header -->
    <div class="task-header">
      <h2 class="task-title">{{ taskTitle }}</h2>
      <div class="task-meta">
        <span class="due-date">Due: {{ formattedDueDate }}</span>
      </div>
      <div v-if="task && task.description" class="task-description">
        {{ task.description }}
      </div>
      <div v-if="isTaskDone" class="status-row">
        <v-chip small color="success" text-color="white" label>
          <v-icon x-small left>mdi-check-circle</v-icon>
          Done
        </v-chip>
      </div>
    </div>

    <div v-if="loadingInventory" class="loading-block">
      <v-skeleton-loader
        v-for="n in 3"
        :key="n"
        type="card"
        height="80"
        class="mb-2"
      />
    </div>

    <!-- ── ASSIGN screen ─────────────────────────────────────────────── -->
    <div v-else-if="step === 'assign'" class="assign-screen">
      <div class="counter-row">
        <span>
          Target: {{ targetUnitsPerType }} device{{
            targetUnitsPerType === 1 ? "" : "s"
          }}
          per unit size/type
        </span>
        <span>·</span>
        <span>{{ totalUnits }} units</span>
        <span>·</span>
        <span>{{ totalDevices }} devices</span>
      </div>

      <!-- Completed type chips -->
      <div v-if="completedTypes.length" class="chips-row">
        <v-chip
          v-for="ct in completedTypes"
          :key="ct.type"
          color="primary"
          outlined
          small
          class="ma-1 chip-clickable"
          @click="openChipSheet(ct.type)"
        >
          {{ ct.label }}
          <span class="chip-count ml-1">
            {{ ct.rows.length }}u · {{ chipDeviceCount(ct) }}d
          </span>
        </v-chip>
      </div>

      <!-- Unit type select -->
      <v-select
        :value="currentType"
        :items="unitTypeOptions"
        label="Unit Size / Type"
        outlined
        dense
        hide-details
        clearable
        class="type-select"
        @input="onSelectType"
      />

      <div v-if="!currentType" class="placeholder">
        Select a Unit Size/Type to begin assigning.
      </div>

      <!-- Confirmed rows -->
      <div v-else>
        <div class="progress-line">
          <span
            :class="{
              'success--text': true,
            }"
          >
            {{ currentTypeDeviceCount }} / {{ targetUnitsPerType }} devices
            assigned
          </span>
        </div>

        <div
          v-for="(row, rowIdx) in rows"
          :key="`row-${rowIdx}`"
          class="row-card row-card-confirmed"
        >
          <div class="row-card-header">
            <span class="unit-label">Unit</span>
            <span class="unit-value">{{ row.unitName }}</span>
          </div>
          <div class="row-card-devices">
            <v-chip
              v-for="(devName, di) in row.deviceNames"
              :key="`d-${rowIdx}-${di}`"
              color="success"
              close
              small
              class="ma-1"
              @click:close="removeDeviceFromRow(rowIdx, di)"
            >
              {{ devName }}
            </v-chip>
          </div>
        </div>

        <!-- Active pending row card -->
        <div class="row-card row-card-pending">
          <v-select
            v-model="pending.unitId"
            :items="
              unitsForCurrentType.map((u) => ({ value: u.id, text: u.name }))
            "
            label="Select Unit"
            outlined
            dense
            hide-details
            class="mb-2"
          />
          <template v-for="(_, devIdx) in pending.deviceIds">
            <v-btn
              :key="`pending-scan-${devIdx}`"
              color="primary"
              small
              block
              outlined
              class="mb-2"
              @click="openScanner(devIdx)"
            >
              <v-icon left small>mdi-qrcode-scan</v-icon>
              Scan Device
            </v-btn>
            <div :key="`pending-or-${devIdx}`" class="d-flex align-center mb-2">
              <v-divider />
              <span class="mx-2 caption grey--text">OR</span>
              <v-divider />
            </div>
            <v-select
              :key="`pending-d-${devIdx}`"
              :value="pending.deviceIds[devIdx]"
              :items="
                devicesAvailableForSlot(devIdx).map((d) => ({
                  value: d.id,
                  text: d.name,
                }))
              "
              label="Select Device"
              outlined
              dense
              hide-details
              clearable
              class="mb-2"
              @input="(v) => setPendingDevice(devIdx, v)"
            />
          </template>
          <v-btn
            block
            color="primary"
            small
            :disabled="completing"
            @click="assignPendingRow"
          >
            <v-icon small left>mdi-plus</v-icon>
            Assign
          </v-btn>
        </div>
      </div>
    </div>

    <!-- ── SUMMARY screen ────────────────────────────────────────────── -->
    <div v-else-if="step === 'summary'" class="summary-screen">
      <div class="summary-title-row">
        <h3 class="summary-title">Assignment Summary</h3>
        <v-btn
          v-if="!isTaskDone"
          icon
          small
          color="primary"
          @click="backToAssign"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </div>

      <div
        v-for="(r, idx) in summaryRows"
        :key="`sum-${idx}`"
        class="summary-row"
      >
        <div class="summary-row-header">
          <span class="summary-unit">{{ r.unitName }}</span>
          <span class="summary-type">{{ r.unitTypeLabel }}</span>
        </div>
        <div class="summary-devices">
          <v-chip
            v-for="(devName, di) in r.deviceNames"
            :key="`sum-d-${idx}-${di}`"
            x-small
            color="success"
            class="ma-1"
          >
            {{ devName }}
          </v-chip>
        </div>
      </div>

      <!-- Send notification (only before done) -->
      <div v-if="!isTaskDone" class="notif-card">
        <div class="notif-title">Send Notification</div>
        <v-text-field
          v-model="notifEmail"
          label="email address"
          type="email"
          outlined
          dense
          hide-details
          class="mb-2"
          :disabled="notifSending || notifSent"
        />
        <div class="d-flex align-center mb-2 text-caption text--secondary">
          <v-icon x-small left>mdi-account-circle</v-icon>
          Assigned by:&nbsp;<strong>{{ assignedByName || "—" }}</strong>
        </div>
        <v-btn
          block
          color="error"
          outlined
          small
          :loading="notifSending"
          :disabled="notifSent"
          @click="sendNotification"
        >
          {{ notifSent ? "Notification sent ✓" : "Send Notification" }}
        </v-btn>
      </div>
    </div>

    <!-- ── QR SCANNER OVERLAY (mirrors A/B mobile pattern) ─────────────── -->
    <div v-if="scannerOpen" class="scanner-overlay">
      <div class="scanner-header">
        <v-btn icon @click="closeScanner">
          <v-icon color="white">mdi-arrow-left</v-icon>
        </v-btn>
        <span>Scan Device QR Code</span>
      </div>

      <div class="scanner-body">
        <QRCodeScanner
          ref="qrScanner"
          :advanced="true"
          @detect-device="onDeviceScanned"
          @detect="onQRDetected"
        />
      </div>

      <div class="scanner-manual-entry pa-3">
        <v-text-field
          v-model="manualScanDeviceId"
          label="Or enter Device ID manually"
          placeholder="e.g. CF00510"
          outlined
          dense
          hide-details
          dark
          class="mb-2"
          @keyup.enter="searchManualScanDevice"
        />
        <v-btn outlined block color="white" @click="searchManualScanDevice">
          Search by ID
        </v-btn>
      </div>
    </div>

    <!-- Chip detail bottom sheet (mobile-native edit/remove) -->
    <v-bottom-sheet :value="chipModal.open" @input="closeChipSheet">
      <v-card v-if="chipModalType">
        <v-card-title class="grey lighten-3 py-2 subtitle-1">
          {{ chipModalType.label }}
          <v-spacer />
          <v-btn icon small @click="closeChipSheet">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <template v-for="(r, ri) in chipModalType.rows">
            <div
              v-for="(devName, di) in r.deviceNames"
              :key="`chip-r-${ri}-d-${di}`"
              class="chip-row"
            >
              <div class="chip-row-info">
                <strong>{{ r.unitName }}</strong>
                <span class="chip-row-devices">{{ devName }}</span>
              </div>
              <v-btn
                icon
                x-small
                color="error"
                @click="removeChipDevice(chipModalType.type, ri, di)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
          </template>
          <v-btn
            block
            color="error"
            small
            class="mt-3"
            @click="removeAllChipEntries(chipModalType.type)"
          >
            Remove All Entries
          </v-btn>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>

    <!-- Sticky action bar -->
    <div v-if="!isTaskDone && !loadingInventory" class="sticky-actions">
      <template v-if="step === 'assign'">
        <v-btn
          text
          color="primary"
          :disabled="!currentTypeMeetsTarget"
          @click="selectNext"
        >
          Select Next
        </v-btn>
        <v-btn
          color="success"
          depressed
          :disabled="!canComplete"
          @click="goToSummary"
        >
          Complete Task
        </v-btn>
      </template>
      <template v-else-if="step === 'summary'">
        <v-btn text @click="backToAssign">Back</v-btn>
        <v-btn
          color="success"
          depressed
          :loading="completing"
          :disabled="completedTypes.length === 0"
          @click="finishTask"
        >
          FINISH
        </v-btn>
      </template>
    </div>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3500"
      bottom
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { UPDATE_TASK } from "../graphql";
import {
  OFFICE_INVENTORY_DEVICES,
  PREINSTALLED_INVENTORY_UNITS,
  SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY,
} from "@components/smart_units/graphql";
import useDevice from "@components/smart_units/useDevice";
import useUser from "@components/authentication/useUser";
import { formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import { formatDeviceName } from "@components/common/helpers/devices";
import { titleCase } from "@tod-ui/helpers/strings";
import QRCodeScanner from "@tod-ui/components/QRCodeScanner.vue";

export default {
  name: "PageMobilePreInstallMethodC",
  components: { QRCodeScanner },
  props: {
    task: { type: Object, required: true },
    facilityId: { type: String, default: null },
  },
  setup() {
    const { currentUser } = useUser();
    return { currentUser };
  },
  data() {
    return {
      step: "assign",
      currentType: null,
      rows: [],
      pending: { unitId: null, deviceIds: [] },
      completedTypes: [],
      chipModal: { open: false, type: null },
      // Inventory
      availableDevices: [],
      vacantUnits: [],
      loadingInventory: false,
      // Notification — "assigned by" is derived from logged-in user (no input)
      notifEmail: "",
      notifSending: false,
      notifSent: false,
      completing: false,
      // QR scanner overlay state — mirrors A/B mobile pattern.
      // scanningForSlotIdx tracks which device slot of the active pending row
      // the scan should populate.
      scannerOpen: false,
      scanningForSlotIdx: null,
      manualScanDeviceId: "",
      snackbar: { show: false, message: "", color: "success" },
    };
  },
  computed: {
    isTaskDone() {
      return this.task && this.task.resolution === "done";
    },
    taskTitle() {
      return (this.task && this.task.title) || "Pre-Install Task";
    },
    formattedDueDate() {
      if (!this.task || !this.task.dueAt) return "";
      return formatDateSlashUTC(this.task.dueAt);
    },
    // Per PRD: target_count = number of UNITS to assign per size/type.
    // Each unit gets exactly one device.
    targetUnitsPerType() {
      const t = this.task && this.task.actions && this.task.actions.targetCount;
      if (t > 0) return t;
      const c =
        this.task && this.task.taskRule && this.task.taskRule.conditions;
      return (c && c.devicesPerUnit) || 1;
    },
    // Group dropdown options by SIZE × TYPE (e.g. "5X5 (Climate Control)")
    // built from the loaded vacant units. Excludes parking units.
    unitTypeOptions() {
      const groups = new Map();
      (this.vacantUnits || []).forEach((u) => {
        const ut = u.unitType || {};
        const typeName = ut.name || "Unknown";
        if (typeName.toLowerCase().includes("parking")) return;
        const sizeLabel = this.unitSizeLabel(ut);
        const key = `${sizeLabel}|${typeName}`;
        const existing = groups.get(key);
        if (existing) {
          existing.vacantCount += 1;
        } else {
          groups.set(key, { key, sizeLabel, typeName, vacantCount: 1 });
        }
      });
      return Array.from(groups.values())
        .sort((a, b) => a.sizeLabel.localeCompare(b.sizeLabel))
        .map((g) => ({
          value: g.key,
          text: `${g.sizeLabel} (${this.titleCase(g.typeName)}) — ${
            g.vacantCount
          }`,
        }));
    },
    unitsForCurrentType() {
      if (!this.currentType) return [];
      // Only exclude units locked into completedTypes — units in the active
      // rows remain selectable so the user can add more devices to them.
      const locked = new Set(
        this.completedTypes
          .flatMap((ct) => ct.rows.map((r) => r.unitId))
          .filter(Boolean)
      );
      return (this.vacantUnits || []).filter((u) => {
        const ut = u.unitType || {};
        const typeName = ut.name || "Unknown";
        if (typeName.toLowerCase().includes("parking")) return false;
        const key = `${this.unitSizeLabel(ut)}|${typeName}`;
        if (key !== this.currentType) return false;
        return !locked.has(u.id);
      });
    },
    totalUnitsForCurrentType() {
      return this.unitsForCurrentType.length;
    },
    // Per-slot device list — excludes pending picks from OTHER slots only,
    // so the current slot's selection stays visible in its v-select.
    devicesAvailableForSlot() {
      return (slotIdx) => {
        const used = new Set();
        this.rows.forEach((r) =>
          (r.deviceIds || []).forEach((id) => id && used.add(id))
        );
        this.completedTypes.forEach((ct) =>
          ct.rows.forEach((r) =>
            (r.deviceIds || []).forEach((id) => id && used.add(id))
          )
        );
        (this.pending.deviceIds || []).forEach((id, i) => {
          if (id && i !== slotIdx) used.add(id);
        });
        return (this.availableDevices || []).filter((d) => !used.has(d.id));
      };
    },
    totalUnits() {
      return (
        this.completedTypes.reduce((s, ct) => s + ct.rows.length, 0) +
        this.rows.length
      );
    },
    totalDevices() {
      const c = (rs) =>
        rs.reduce((s, r) => s + (r.deviceIds || []).filter(Boolean).length, 0);
      return (
        this.completedTypes.reduce((s, ct) => s + c(ct.rows), 0) + c(this.rows)
      );
    },
    hasCurrentRows() {
      return this.rows.length > 0;
    },
    currentTypeDeviceCount() {
      return this.rows.reduce(
        (s, r) => s + (r.deviceIds || []).filter(Boolean).length,
        0
      );
    },
    currentTypeMeetsTarget() {
      return this.rows.length > 0;
    },
    canComplete() {
      const currentHasRows = this.currentType && this.hasCurrentRows;
      return this.completedTypes.length > 0 || currentHasRows;
    },
    summaryRows() {
      const out = [];
      this.completedTypes.forEach((ct) =>
        ct.rows.forEach((r) => out.push({ unitTypeLabel: ct.label, ...r }))
      );
      return out;
    },
    chipModalType() {
      return this.completedTypes.find((c) => c.type === this.chipModal.type);
    },

    // "Assigned by" is the logged-in user — sent as `doneBy` on the email
    // mutation, no separate input.
    assignedByName() {
      const u = this.currentUser;
      if (!u) return "";
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || u.username || "";
    },
  },
  watch: {
    task: {
      handler(newTask) {
        if (!newTask) return;
        this.hydrateFromSavedAssignments();
      },
      immediate: true,
      deep: true,
    },
    facilityId: {
      handler(fid) {
        if (fid) this.loadInventory(fid);
      },
      immediate: true,
    },
    devicesPerUnit() {
      this.resetPending();
    },
  },
  methods: {
    titleCase,

    async loadInventory(facilityId) {
      const fid =
        facilityId ||
        (this.task && this.task.organization && this.task.organization.id);
      if (!fid) return;
      this.loadingInventory = true;
      try {
        const [devicesResult, unitsResult] = await Promise.all([
          this.$apollo.query({
            query: OFFICE_INVENTORY_DEVICES,
            variables: {
              id: fid,
              availableInventory: true,
              dispositions: ["in_service", "waiting"],
              statuses: ["ok"],
              organizationAssigned: false,
              limit: 200,
              page: 1,
            },
            fetchPolicy: "network-only",
          }),
          this.$apollo.query({
            query: PREINSTALLED_INVENTORY_UNITS,
            variables: {
              id: fid,
              assigned: false,
              deviceAssigned: false,
              type: "zone",
              subtype: "unit",
              limit: 500,
              page: 1,
              sort: "name asc",
            },
            fetchPolicy: "network-only",
          }),
        ]);

        const devs =
          (devicesResult.data &&
            devicesResult.data.organizationDevicesPaginated &&
            devicesResult.data.organizationDevicesPaginated.data) ||
          [];
        this.availableDevices = devs
          .filter(
            (d) =>
              d.disposition === "in_service" &&
              d.status === "ok" &&
              !d.rmaRequest
          )
          .map((d) => ({
            // Spread MUST come first — if the raw device record has
            // `name: null`, putting the spread last would overwrite the
            // explicit fallback below and the dropdown would show "null".
            ...d,
            id: d.id,
            name: formatDeviceName(d) || d.shortId || d.externalId || d.id,
          }));

        this.vacantUnits =
          (unitsResult.data &&
            unitsResult.data.organizationDescendantsPaginated &&
            unitsResult.data.organizationDescendantsPaginated.data) ||
          [];

        this.resetPending();
      } catch (err) {
        console.error("Method C mobile: inventory load failed", err);
        this.toast("error", "Failed to load devices/units.");
      } finally {
        this.loadingInventory = false;
      }
    },

    // Re-enter a previously completed task (resolution: "done") in summary view.
    hydrateFromSavedAssignments() {
      const saved =
        this.task && this.task.actions && this.task.actions.assignmentsByType;
      if (!Array.isArray(saved) || saved.length === 0) return;

      // Group flat saved entries back into completedTypes structure.
      const map = new Map();
      saved.forEach((entry) => {
        const t = entry.unitType || entry.unit_type || "unknown";
        if (!map.has(t)) {
          map.set(t, { type: t, label: this.chipLabel(t), rows: [] });
        }
        map.get(t).rows.push({
          unitId: entry.unitId || entry.unit_id,
          unitName: entry.unitName || entry.unit_name,
          deviceIds: entry.deviceIds || entry.device_ids || [],
          deviceNames: entry.deviceNames || entry.device_names || [],
        });
      });
      this.completedTypes = Array.from(map.values());
      // For done tasks, jump straight to summary (read-only).
      if (this.isTaskDone) this.step = "summary";
    },

    resetPending() {
      // Always 1 device slot — Method C is now "1 device per unit, target N
      // units per size/type".
      this.pending = {
        unitId: null,
        deviceIds: [null],
      };
    },

    // Build a "WxL" size label from a unitType. Falls back to extracting from
    // the type name.
    unitSizeLabel(unitType) {
      if (!unitType) return "Unknown";
      const w = unitType.width;
      const l = unitType.length;
      if (w && l) return `${w}X${l}`;
      const m =
        typeof unitType.name === "string"
          ? unitType.name.match(/(\d+)\s*[xX]\s*(\d+)/)
          : null;
      if (m) return `${m[1]}X${m[2]}`;
      return "Unsized";
    },

    // Composite chip key "5X5|Climate Control" → "5X5 (Climate Control)".
    chipLabel(key) {
      if (!key) return "";
      const idx = key.indexOf("|");
      if (idx === -1) return this.titleCase(key);
      return `${key.slice(0, idx)} (${this.titleCase(key.slice(idx + 1))})`;
    },

    // Vue 2 doesn't track `arr[i] = v` by index, so use $set so the v-select
    // re-renders with the picked value.
    setPendingDevice(idx, value) {
      this.$set(this.pending.deviceIds, idx, value);
    },

    onSelectType(type) {
      this.currentType = type || null;
      const existing = this.completedTypes.find((c) => c.type === type);
      if (existing) {
        this.rows = existing.rows.map((r) => ({
          unitId: r.unitId,
          unitName: r.unitName,
          deviceIds: [...(r.deviceIds || [])],
          deviceNames: [...(r.deviceNames || [])],
        }));
        this.completedTypes = this.completedTypes.filter(
          (c) => c.type !== type
        );
      } else {
        this.rows = [];
      }
      this.resetPending();
    },

    assignPendingRow() {
      const p = this.pending;
      if (!p.unitId) {
        this.toast("warning", "Please select a unit.");
        return;
      }
      const chosen = (p.deviceIds || []).filter(Boolean);
      if (chosen.length === 0) {
        this.toast("warning", "Please select at least one device.");
        return;
      }
      const unit = (this.vacantUnits || []).find((u) => u.id === p.unitId);
      const unitName = (unit && unit.name) || `Unit ${p.unitId}`;
      const deviceNames = chosen.map((id) => {
        const d = (this.availableDevices || []).find((x) => x.id === id);
        return (d && d.name) || id;
      });
      const existingRowIdx = this.rows.findIndex((r) => r.unitId === p.unitId);
      if (existingRowIdx >= 0) {
        // Replace the whole row so Vue 2 detects the nested change.
        const existing = this.rows[existingRowIdx];
        this.$set(this.rows, existingRowIdx, {
          ...existing,
          deviceIds: [...existing.deviceIds, ...chosen],
          deviceNames: [...existing.deviceNames, ...deviceNames],
        });
      } else {
        this.rows.push({
          unitId: p.unitId,
          unitName,
          deviceIds: chosen,
          deviceNames,
        });
      }
      this.resetPending();
    },

    removeDeviceFromRow(rowIdx, deviceIdx) {
      const row = this.rows[rowIdx];
      if (!row) return;
      row.deviceIds.splice(deviceIdx, 1);
      row.deviceNames.splice(deviceIdx, 1);
      if (row.deviceIds.length === 0) this.rows.splice(rowIdx, 1);
    },

    selectNext() {
      if (!this.currentType || !this.hasCurrentRows) {
        this.toast(
          "warning",
          "Please select a unit and assign at least one device first."
        );
        return;
      }
      this.saveCurrentTypeAsChip();
      this.currentType = null;
      this.rows = [];
      this.resetPending();
    },

    saveCurrentTypeAsChip() {
      if (!this.currentType || !this.hasCurrentRows) return;
      const chip = {
        type: this.currentType,
        label: this.chipLabel(this.currentType),
        effectiveTarget: Math.min(
          this.targetUnitsPerType,
          this.totalUnitsForCurrentType
        ),
        rows: this.rows.map((r) => ({
          unitId: r.unitId,
          unitName: r.unitName,
          deviceIds: [...(r.deviceIds || [])],
          deviceNames: [...(r.deviceNames || [])],
        })),
      };
      const idx = this.completedTypes.findIndex((c) => c.type === chip.type);
      if (idx >= 0) this.completedTypes.splice(idx, 1, chip);
      else this.completedTypes.push(chip);
    },

    chipDeviceCount(ct) {
      return ct.rows.reduce(
        (s, r) => s + (r.deviceIds || []).filter(Boolean).length,
        0
      );
    },

    openChipSheet(type) {
      this.chipModal = { open: true, type };
    },
    closeChipSheet() {
      this.chipModal = { open: false, type: null };
    },
    removeChipDevice(type, rowIdx, deviceIdx) {
      const ct = this.completedTypes.find((c) => c.type === type);
      if (!ct) return;
      const row = ct.rows[rowIdx];
      if (!row) return;
      row.deviceIds.splice(deviceIdx, 1);
      row.deviceNames.splice(deviceIdx, 1);
      if (row.deviceIds.length === 0) {
        ct.rows.splice(rowIdx, 1);
      }
      if (ct.rows.length === 0) {
        this.completedTypes = this.completedTypes.filter(
          (c) => c.type !== type
        );
        this.closeChipSheet();
      }
    },
    removeAllChipEntries(type) {
      if (!window.confirm("Remove all entries for this unit type?")) return;
      this.completedTypes = this.completedTypes.filter((c) => c.type !== type);
      this.closeChipSheet();
    },

    goToSummary() {
      if (this.currentType && this.hasCurrentRows) {
        this.saveCurrentTypeAsChip();
        this.currentType = null;
        this.rows = [];
        this.resetPending();
      }
      if (this.completedTypes.length === 0) {
        this.toast(
          "warning",
          "Please assign at least one unit before completing."
        );
        return;
      }
      this.step = "summary";
    },
    backToAssign() {
      this.step = "assign";
    },

    async sendNotification() {
      const email = (this.notifEmail || "").trim();
      const by = (this.assignedByName || "").trim() || "Unknown User";
      if (!email) return this.toast("warning", "Enter an email address.");
      if (this.completedTypes.length === 0)
        return this.toast("warning", "No assignments to send.");

      this.notifSending = true;
      const devicesDetails = [];
      this.completedTypes.forEach((ct) =>
        ct.rows.forEach((r) =>
          (r.deviceNames || []).forEach((devName) => {
            if (devName) {
              devicesDetails.push({
                unitName: r.unitName,
                deviceName: devName,
              });
            }
          })
        )
      );

      try {
        await this.$apollo.mutate({
          mutation: SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY,
          variables: {
            facilityId: this.task.organization.id,
            doneBy: by,
            emails: [email],
            devicesDetails,
          },
        });
        this.notifSent = true;
        this.toast("success", `Notification sent to ${email}.`);
      } catch (error) {
        console.error("Method C mobile: sendNotification failed", error);
        const gql = error.graphQLErrors && error.graphQLErrors.length > 0;
        const msg = gql
          ? error.graphQLErrors[0].message
          : error.message || "Failed to send notification.";
        this.toast("error", msg);
      } finally {
        this.notifSending = false;
      }
    },

    async finishTask() {
      if (!this.task || this.completedTypes.length === 0) return;
      this.completing = true;

      try {
        // 1. Move each device to its assigned unit — in parallel. Sequential
        // awaits added ~300ms per device on slow mobile connections; with
        // 5+ devices that's noticeable lag before the completion screen.
        const moves = [];
        this.completedTypes.forEach((ct) =>
          ct.rows.forEach((r) =>
            (r.deviceIds || []).forEach((did) => {
              const device = (this.availableDevices || []).find(
                (d) => d.id === did
              );
              if (device) moves.push({ device, unitId: r.unitId });
            })
          )
        );
        await Promise.all(
          moves.map(({ device, unitId }) => {
            const { moveDevice } = useDevice(device);
            return moveDevice(this.$apollo, unitId, null);
          })
        );
        // 2. Build assignmentsByType payload.
        const payload = [];
        this.completedTypes.forEach((ct) =>
          ct.rows.forEach((r) =>
            payload.push({
              unitType: ct.type,
              unitId: r.unitId,
              unitName: r.unitName,
              deviceIds: [...(r.deviceIds || [])],
              deviceNames: [...(r.deviceNames || [])],
            })
          )
        );

        // Preserve existing checklist
        const existingChecklist =
          this.task.actions && Array.isArray(this.task.actions.checklist)
            ? this.task.actions.checklist.map((item) => ({
                id: item.id,
                completed: item.completed || false,
                comment: item.comment || "",
                description: item.description || "",
              }))
            : [];

        await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.task.id,
            assigneeId: this.task.assignee && this.task.assignee.id,
            description: this.task.description,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution: "done",
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
            actions: {
              checklist: existingChecklist,
              assignmentsByType: payload,
            },
          },
        });

        this.$emit("task-updated", {
          type: "complete_preinstall_task",
          taskId: this.task.id,
          assignedCount: payload.length,
          resolution: "done",
        });

        this.completedTypes = [];
        this.rows = [];
        this.currentType = null;
        this.notifEmail = "";
        this.notifSent = false;
        this.resetPending();
        this.finishAndClose();
      } catch (error) {
        console.error("Method C mobile: finishTask failed", error);
        const gql = error.graphQLErrors && error.graphQLErrors.length > 0;
        const msg = gql
          ? error.graphQLErrors[0].message
          : error.message || "Failed to complete task.";
        this.toast("error", msg);
      } finally {
        this.completing = false;
      }
    },

    finishAndClose() {
      window.close();
      setTimeout(() => {
        window.location.href = "/";
      }, 500);
    },

    // ── QR scanner ────────────────────────────────────────────────────
    // Mirrors PageMobilePreInstallThreshold's scanner flow but writes to the
    // active pending row's device slot via setPendingDevice/$set so Vue 2
    // sees the array index update.

    openScanner(slotIdx) {
      if (!this.currentType) {
        this.toast("warning", "Select a unit size/type first.");
        return;
      }
      if (!this.pending.unitId) {
        this.toast("warning", "Select a unit before scanning a device.");
        return;
      }
      this.scanningForSlotIdx = slotIdx;
      this.manualScanDeviceId = "";
      this.scannerOpen = true;
    },

    closeScanner() {
      this.scannerOpen = false;
      this.scanningForSlotIdx = null;
      this.manualScanDeviceId = "";
    },

    onDeviceScanned(deviceData) {
      const device = (this.availableDevices || []).find(
        (d) => d.externalId === deviceData.externalId || d.id === deviceData.id
      );
      if (device) {
        this.onScanDeviceFound(device);
      } else {
        this.toast("error", "Device not found in office inventory");
      }
    },

    onQRDetected(qrData) {
      const match = ((qrData && qrData.text) || "").match(/([A-Z0-9-]+)/);
      const deviceId = match ? match[1] : null;
      if (deviceId) this.searchScanDeviceByShortId(deviceId);
    },

    searchManualScanDevice() {
      const id = (this.manualScanDeviceId || "").trim();
      if (!id) {
        this.toast("warning", "Please enter a device ID");
        return;
      }
      this.searchScanDeviceByShortId(id);
    },

    searchScanDeviceByShortId(deviceId) {
      const lower = deviceId.toLowerCase();
      const device = (this.availableDevices || []).find(
        (d) =>
          (d.externalId && d.externalId.toLowerCase().includes(lower)) ||
          (d.shortId && d.shortId.toLowerCase() === lower) ||
          d.id === deviceId
      );
      if (device) {
        this.onScanDeviceFound(device);
      } else {
        this.toast(
          "error",
          `Device "${deviceId}" not found in office inventory`
        );
      }
    },

    onScanDeviceFound(device) {
      const idx = this.scanningForSlotIdx;
      if (idx === null || idx === undefined) return;

      // Reject if device is already used elsewhere (other slot in pending row,
      // or any confirmed/completed row). devicesAvailableForSlot encodes that
      // logic — reuse it so we stay consistent with the dropdown.
      const allowed = this.devicesAvailableForSlot(idx);
      if (!allowed.some((d) => d.id === device.id)) {
        this.toast(
          "warning",
          `${
            device.name || device.shortId || "Device"
          } is already assigned or unavailable`
        );
        return;
      }

      this.setPendingDevice(idx, device.id);
      this.closeScanner();
      this.toast(
        "success",
        `${device.name || device.shortId || "Device"} scanned`
      );
    },

    toast(color, message) {
      this.snackbar = { show: true, color, message };
    },
  },
};
</script>

<style scoped>
.mobile-method-c {
  padding: 12px 12px 80px;
  max-width: 100vw;
}
.task-header {
  margin-bottom: 12px;
}
.task-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 4px;
}
.task-meta {
  color: #666;
  font-size: 13px;
}
.task-description {
  font-size: 13px;
  color: #555;
  margin-top: 4px;
}
.status-row {
  margin-top: 8px;
}
.counter-row {
  font-size: 12px;
  color: #757575;
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.chips-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.chip-clickable {
  cursor: pointer;
}
.chip-count {
  background: #5b8dd9;
  color: #fff;
  border-radius: 8px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
}
.type-select {
  margin-bottom: 12px;
}
.placeholder {
  font-style: italic;
  color: #777;
  padding: 12px 0;
  border-left: 3px solid #ddd;
  padding-left: 10px;
}
.scanner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  z-index: 999;
  display: flex;
  flex-direction: column;
}
.scanner-header {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.85);
  padding: 12px 16px;
  color: white;
  font-size: 16px;
  font-weight: 500;
  flex-shrink: 0;
}
.scanner-body {
  flex: 1;
  overflow: hidden;
}
.scanner-manual-entry {
  background: rgba(0, 0, 0, 0.85);
  flex-shrink: 0;
}
.progress-line {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #f5f5f5;
  border-radius: 6px;
}
.row-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
}
.row-card-confirmed {
  border-color: #1976d2;
  background: #f5f9ff;
}
.row-card-header {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 6px;
}
.unit-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1976d2;
  font-weight: 600;
}
.unit-value {
  font-weight: 700;
  font-size: 15px;
}
.row-card-devices {
  display: flex;
  flex-wrap: wrap;
}
.row-card-pending {
  border-style: dashed;
}
.summary-screen {
  padding-bottom: 20px;
}
.summary-title-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.summary-title {
  font-size: 16px;
  font-weight: 700;
  text-decoration: underline;
  margin: 0;
  flex: 1;
}
.summary-row {
  border-bottom: 1px solid #eee;
  padding: 8px 0;
}
.summary-row-header {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}
.summary-unit {
  font-weight: 700;
}
.summary-type {
  color: #666;
  font-size: 12px;
}
.summary-devices {
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
}
.notif-card {
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}
.notif-title {
  font-weight: 700;
  margin-bottom: 8px;
}
.chip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}
.chip-row-info {
  display: flex;
  flex-direction: column;
}
.chip-row-devices {
  font-size: 12px;
  color: #666;
}
.sticky-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e0e0e0;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  z-index: 5;
}
.loading-block {
  margin-top: 12px;
}
</style>
